import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
} from '@angular/core';

/**
 * Styles STANDALONE CSV/PDF download offers (an anchor that is its whole paragraph/list item —
 * the "download it here" line after a reply) as small secondary buttons. Links referenced
 * inline mid-sentence keep their plain hyperlink styling (designer spec, SD-9246 follow-up).
 */
@Directive({
    selector: '[appGableDownloadLinks]',
    standalone: true,
})
export class GableDownloadLinkDirective implements AfterViewInit, OnChanges, OnDestroy {
    /** Bound to the message text so a rescan fires whenever streamed content changes. */
    @Input() appGableDownloadLinks: string | null = null;

    private _raf: number | null = null;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _zone: NgZone,
    ) {}

    ngAfterViewInit(): void {
        this._schedule();
    }

    ngOnChanges(): void {
        this._schedule();
    }

    ngOnDestroy(): void {
        this._cancel();
    }

    private _schedule(): void {
        this._cancel();
        this._zone.runOutsideAngular(() => {
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this._apply();
            });
        });
    }

    private _cancel(): void {
        if (this._raf !== null) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    private _apply(): void {
        const anchors = this._el.nativeElement.querySelectorAll<HTMLAnchorElement>('a[href]');
        anchors.forEach((a) => this._styleIfDownloadOffer(a));
    }

    private _styleIfDownloadOffer(a: HTMLAnchorElement): void {
        if (a.classList.contains('gable-download-link-btn')) {
            return;
        }
        const href = a.getAttribute('href') ?? '';
        const text = (a.textContent ?? '').trim();
        const isFileLink = /\.(csv|pdf)(\?|#|$)/i.test(href);
        const looksLikeOffer = /\b(download|export|save)\b/i.test(text) && /\b(csv|pdf)\b/i.test(text);
        if (!isFileLink && !looksLikeOffer) {
            return;
        }
        // Only a standalone offer becomes a button; links woven into a sentence stay plain
        // hyperlinks. Several offers can share one block, which is how the model often
        // writes two formats of the same report, so what must be absent is prose.
        const block = a.closest('p, li');
        if (!block || !this._blockIsOnlyOffers(block)) {
            return;
        }
        a.classList.add('gable-download-link-btn');
        const icon = document.createElement('i');
        icon.className = 'ph-bold ph-download-simple icon-16';
        icon.setAttribute('aria-hidden', 'true');
        a.prepend(icon);
    }

    /** True when the block holds nothing but download anchors and the punctuation between them. */
    private _blockIsOnlyOffers(block: Element): boolean {
        const anchors = Array.from(block.querySelectorAll('a[href]'));
        if (anchors.length === 0) {
            return false;
        }

        let remaining = (block.textContent ?? '');
        anchors.forEach((anchor) => {
            remaining = remaining.replace((anchor.textContent ?? '').trim(), ' ');
        });

        return remaining.replace(/[|·•,;/\s]|\band\b|\bor\b/gi, '') === '';
    }
}
